import { useFormik } from "formik";
import * as yup from "yup";
import { Box, Grid, FormControl, MenuItem } from "@mui/material";

import { StyledTextField } from "@stories/atoms/StyledTextField/StyledTextField";
import { StyledButton } from "@stories/atoms/StyledButton/StyledButton";
import {
  SocialCommitmentLineItem,
  SocialCommitmentTypeHour,
  SocialCommitmentTypeSpend,
  TargetUnit,
  socialCommitmentTypeToString,
} from "@interfaces/commitment";

interface TargetFormProps {
  loading: boolean;
  socialCommitment: SocialCommitmentLineItem;
  existingSocialCommitments: SocialCommitmentLineItem[];
  handleSubmit: (socialCommitment: SocialCommitmentLineItem) => void;
}

export const TargetForm = ({
  loading,
  socialCommitment,
  existingSocialCommitments,
  handleSubmit,
}: TargetFormProps) => {
  const validationSchema = yup.object({
    targetName: yup.string().required("First name is required"),
    targetDescription: yup
      .string()
      .test("Description required", "Description required", function (item) {
        if (
          this.parent.targetName === SocialCommitmentTypeSpend.DefaultSpend ||
          this.parent.targetName === SocialCommitmentTypeHour.DefaultHour
        ) {
          if (item && item.length > 0) {
            return true;
          }
          return false;
        }
        return true;
      }),
  });
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      targetName: socialCommitment.targetName,
      targetDescription: socialCommitment.targetDescription,
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      handleSubmit({
        ...socialCommitment,
        targetName: values.targetName,
        targetDescription: values.targetDescription,
      });
    },
  });

  let targetOptions: (SocialCommitmentTypeSpend | SocialCommitmentTypeHour)[] =
    [];
  if (socialCommitment.targetUnit === TargetUnit.Spend) {
    targetOptions = Object.values(SocialCommitmentTypeSpend).filter(
      (sc) => !existingSocialCommitments.find((e) => e.targetName === sc)
    );

    if (targetOptions.indexOf(SocialCommitmentTypeSpend.DefaultSpend) === -1) {
      targetOptions.unshift(SocialCommitmentTypeSpend.DefaultSpend);
    }
  } else {
    targetOptions = Object.values(SocialCommitmentTypeHour).filter(
      (sc) => !existingSocialCommitments.find((e) => e.targetName === sc)
    );

    if (targetOptions.indexOf(SocialCommitmentTypeHour.DefaultHour) === -1) {
      targetOptions.unshift(SocialCommitmentTypeHour.DefaultHour);
    }
  }
  return (
    <Box>
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={6} sm={6}>
            <FormControl fullWidth>
              <StyledTextField
                select
                id="targetName"
                name="targetName"
                margin="dense"
                value={formik.values.targetName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.targetName && Boolean(formik.errors.targetName)
                }
                helperText={
                  formik.touched.targetName ? formik.errors.targetName : ""
                }
                label="Target Name"
              >
                {targetOptions.map((o) => (
                  <MenuItem key={o} value={o}>
                    {socialCommitmentTypeToString(o)}
                  </MenuItem>
                ))}
              </StyledTextField>
            </FormControl>
          </Grid>
          <Grid item xs={6} sm={6}>
            <FormControl fullWidth>
              <StyledTextField
                id="targetDescription"
                name="targetDescription"
                margin="dense"
                disabled={
                  formik.values.targetName !==
                    SocialCommitmentTypeSpend.DefaultSpend &&
                  formik.values.targetName !==
                    SocialCommitmentTypeHour.DefaultHour
                }
                value={formik.values.targetDescription}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.targetDescription &&
                  Boolean(formik.errors.targetDescription)
                }
                helperText={
                  formik.touched.targetDescription
                    ? formik.errors.targetDescription
                    : ""
                }
                label="Target Description"
              />
            </FormControl>
          </Grid>
          <Grid
            item
            xs={12}
            sm={12}
            sx={{
              display: "flex !important",
              justifyContent: "right !important;",
            }}
          >
            <StyledButton
              variant="contained"
              type="submit"
              disabled={loading || formik.isSubmitting}
            >
              Save
            </StyledButton>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};
